import { Form } from 'react-bootstrap';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import {
    RECURRENT_COUNT_MAX,
    RECURRENT_COUNT_MIN,
    RECURRENT_FREQUENCIES,
    RECURRENT_INTERVAL_MAX,
    RECURRENT_INTERVAL_MIN,
    RECURRENT_WEEKDAYS,
    type RecurrentData,
    type RecurrentFormValue,
    type RecurrentWeekday,
} from '@econtractor/types';
import { emptyRecurrentData, toggleWeekday, withFrequency } from './emptyRecurrent';

interface RecurrentFormProps {
    idPrefix: string;
    value: RecurrentFormValue;
    onChange: (next: RecurrentFormValue) => void;
    disabled?: boolean;
    showAllowOverlap: boolean;
    monthDayHint: number;
}

const RecurrentForm = ({
    idPrefix,
    value,
    onChange,
    disabled = false,
    showAllowOverlap,
    monthDayHint,
}: RecurrentFormProps) => {
    const { t } = useTranslation(['Tasks']);
    const data = value.data || emptyRecurrentData();

    const patch = (next: Partial<RecurrentData>) => {
        onChange({ ...value, enabled: true, data: { ...data, ...next } });
    };

    return (
        <div>
            <div className="form-check form-switch mb-3">
                <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id={`${idPrefix}-enabled`}
                    checked={value.enabled}
                    disabled={disabled}
                    onChange={(e) => {
                        const enabled = e.target.checked;
                        onChange({
                            enabled,
                            data: enabled ? value.data || emptyRecurrentData() : null,
                            allowOverlap: enabled ? value.allowOverlap : false,
                        });
                    }}
                />
                <label className="form-check-label" htmlFor={`${idPrefix}-enabled`}>
                    <span className="d-block">{t('recurrent.enable')}</span>
                    <span className="d-block text-muted fs-12">{t('recurrent.enableHint')}</span>
                </label>
            </div>

            {value.enabled && (
                <>
                    <div className="d-flex gap-2 mb-3">
                        <Form.Group className="flex-grow-1">
                            <label className="form-label text-muted">{t('recurrent.interval')}</label>
                            <Form.Control
                                type="number"
                                min={RECURRENT_INTERVAL_MIN}
                                max={RECURRENT_INTERVAL_MAX}
                                disabled={disabled}
                                value={data.interval}
                                onChange={(e) => patch({ interval: Number(e.target.value) })}
                            />
                        </Form.Group>
                        <Form.Group className="flex-grow-1">
                            <label className="form-label text-muted">{t('recurrent.frequency')}</label>
                            <Form.Select
                                disabled={disabled}
                                value={data.frequency}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        data: withFrequency(data, e.target.value as RecurrentData['frequency'], monthDayHint),
                                    })
                                }
                            >
                                {RECURRENT_FREQUENCIES.map((frequency) => (
                                    <option key={frequency} value={frequency}>
                                        {t(`recurrent.freq.${frequency}`)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>

                    {data.frequency === 'week' && (
                        <div className="mb-3">
                            <div className="form-label text-muted">{t('recurrent.weekdays')}</div>
                            <div className="d-flex flex-wrap gap-1">
                                {RECURRENT_WEEKDAYS.map((day) => {
                                    const active = data.weekdays.includes(day);
                                    return (
                                        <button
                                            key={day}
                                            type="button"
                                            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            disabled={disabled}
                                            onClick={() => patch({ weekdays: toggleWeekday(data.weekdays, day as RecurrentWeekday) })}
                                        >
                                            {t(`recurrent.weekday.${day}`)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {data.frequency === 'month' && (
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('recurrent.monthDay')}</label>
                            <Form.Control
                                type="number"
                                min={1}
                                max={31}
                                disabled={disabled}
                                value={data.monthDay ?? ''}
                                onChange={(e) => patch({ monthDay: Number(e.target.value) })}
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('recurrent.end')}</label>
                        <Form.Select
                            disabled={disabled}
                            value={data.endType}
                            onChange={(e) => {
                                const endType = e.target.value as RecurrentData['endType'];
                                patch({
                                    endType,
                                    endDate: endType === 'date' ? data.endDate : null,
                                    endCount: endType === 'count' ? data.endCount || 1 : null,
                                });
                            }}
                        >
                            <option value="never">{t('recurrent.endNever')}</option>
                            <option value="date">{t('recurrent.endDate')}</option>
                            <option value="count">{t('recurrent.endCount')}</option>
                        </Form.Select>
                    </Form.Group>

                    {data.endType === 'date' && (
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('recurrent.endDate')}</label>
                            <AppDatePicker
                                allowClear
                                disabled={disabled}
                                value={
                                    data.endDate
                                        ? DT.fromMillis(data.endDate, { zone: 'local' }).toISODate() || ''
                                        : ''
                                }
                                onChange={(iso) => {
                                    const day = DT.fromISO(iso, { zone: 'local' });
                                    patch({ endDate: day.isValid ? day.endOf('day').toMillis() : null });
                                }}
                            />
                        </Form.Group>
                    )}

                    {data.endType === 'count' && (
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('recurrent.endCount')}</label>
                            <Form.Control
                                type="number"
                                min={RECURRENT_COUNT_MIN}
                                max={RECURRENT_COUNT_MAX}
                                disabled={disabled}
                                value={data.endCount ?? ''}
                                onChange={(e) => patch({ endCount: Number(e.target.value) })}
                            />
                            <div className="text-muted fs-12 mt-1">{t('recurrent.endCountHint')}</div>
                        </Form.Group>
                    )}

                    {showAllowOverlap && (
                        <div className="form-check form-switch mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                role="switch"
                                id={`${idPrefix}-overlap`}
                                checked={value.allowOverlap}
                                disabled={disabled}
                                onChange={(e) => onChange({ ...value, allowOverlap: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor={`${idPrefix}-overlap`}>
                                <span className="d-block">{t('recurrent.allowOverlap')}</span>
                                <span className="d-block text-muted fs-12">{t('recurrent.allowOverlapHint')}</span>
                            </label>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RecurrentForm;
